import React, { useState, useEffect } from 'react'
import s from '@corratech/pylot-custom-widgets/Products/products.module.scss'
import { arrayOf, oneOf, string, object } from 'prop-types'
import dynamic from 'next/dynamic'
const SlickSlider = dynamic(() => import('react-slick'))
import Gallery from '@corratech/pylot-rich-content/src/Gallery'
import { useQuery } from '@pylot-data/hooks/use-query'
import { GET_PRODUCTS_BY_SKU_QUERY } from '@components/alfakher/pylot-rich-content/src/Queries/getProductsBySku'
import { ProductItem as ProductTile } from '@components/common/ProductTile/ProductTile'
import Skeleton from 'react-loading-skeleton'
import cn from 'classnames'
import { getFastlyUrl } from '@corratech/pylot-image'
import { useDeviceType } from '@components/common/IsMobile/useDeviceType'
import { Loader } from '@components/icons'
import { Button } from '@corratech/pylot-ui'
import { useRouter } from 'next/router'
import Link from 'next/link'

/**
 * Sort products based on the original order of SKUs
 *
 * @param {Array} skus
 * @param {Array} products
 * @returns {Array}
 */
const restoreSortOrder = (skus, products) => {
    const productsBySku = new Map()
    products?.forEach((product) => {
        productsBySku.set(product.sku, product)
    })
    return skus.map((sku) => productsBySku.get(sku)).filter(Boolean)
}

const LoaderTile = () => (
    <div className={s.productSlider}>
        <div className={cn(s.productSliderDefault, 'productSliderDefault')}>
            <h4 className={s.carouselHeading}>
                <Skeleton width={320} />
            </h4>
            <div className={s['productitem-skeleton']}>
                <Skeleton height={320} className={s['product-skeleton-img']} />
                <Skeleton height={320} className={s['product-skeleton-img']} />
                <Skeleton height={320} className={s['product-skeleton-img']} />
                <Skeleton height={320} className={s['product-skeleton-img']} />
            </div>
        </div>
    </div>
)

const LoaderComponent = () => (
    <div className={s['loader-wrapper']}>
        <Loader variant="plp-loader" />
    </div>
)

/**
 * Page Builder Products component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Products
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays a Products based on a number of skus.
 */
const Products = (props) => {
    const { isMobile, isTablet } = useDeviceType()
    const t = (string) => string

    const {
        enabled,
        appearance,
        skus,
        AddToWishlist,
        AddToWishlistLabel,
        productQuery,
        globalOptions = {},
        imageQuality,
        brandMode,
        brandBg,
        brandBgMobile
    } = props

    const PAGE_SIZE = props.isLoadMoreEnabled ? 12 : skus.length
    const [currentPage, setCurrentPage] = useState(1)
    const [loadedItems, setLoadedItems] = useState([])
    const router = useRouter()

    const paginatedSkus = props.isLoadMoreEnabled
        ? skus.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
        : skus

    const hasMoreSkus =
        props.isLoadMoreEnabled && currentPage * PAGE_SIZE < skus.length

    const { data, error, isValidating } = useQuery(
        productQuery,
        { skus: paginatedSkus },
        { revalidateOnFocus: false }
    )

    useEffect(() => {
        if (!props.isLoadMoreEnabled) return

        if (!data?.data?.products?.items?.length) return

        const newItems = restoreSortOrder(
            paginatedSkus,
            data.data.products.items
        )

        setLoadedItems((prev) => {
            const seen = new Set(prev.map((p) => p.sku))
            const deduped = [
                ...prev,
                ...newItems.filter((p) => !seen.has(p?.sku))
            ]
            return deduped
        })
    }, [data, currentPage, PAGE_SIZE, skus, props.isLoadMoreEnabled])

    const productsToRender = props.isLoadMoreEnabled
        ? loadedItems
        : restoreSortOrder(skus, data?.data?.products?.items || [])

    if (!skus?.length) return renderNoProducts()

    if (!productsToRender.length && !data && !error && currentPage === 1) {
        return LoaderTile()
    }

    if (error) return renderNoProducts()

    const renderNoProducts = () => (
        <div className={s.root}>
            <div className={s.error}>
                {t('No products to display')}
                {skus.length ? (
                    <>
                        {t('Should be requested products with SKUs')}{' '}
                        {skus.join(', ')}
                    </>
                ) : (
                    ''
                )}
            </div>
        </div>
    )

    const renderContents = () => {
        if (brandMode === 'logo' || brandMode === 'text') {
            return (
                <div className={s['brandContentWrapper']}>
                    {!props?.brandContent && (
                        <p className={s['brandContentText']}>
                            {props?.brandContent}
                        </p>
                    )}
                    <div className={s['brandContainer']}>
                        {brandMode === 'logo' && props.brandLogo && (
                            <img
                                className={s['brandLogo']}
                                src={getFastlyUrl({
                                    src: props?.brandLogo,
                                    width: 200
                                })}
                                alt={props?.brandLogo}
                                loading="lazy"
                            />
                        )}
                        {props?.brandHeading && (
                            <h2 className={s['brandHeading']}>
                                {props?.brandHeading}
                            </h2>
                        )}
                    </div>
                </div>
            )
        }
        return (
            props?.brandHeading && (
                <h4 className={s.carouselHeading}>{props?.brandHeading}</h4>
            )
        )
    }

    const renderLogoOrText = () => {
        if (brandMode === 'logo' || brandMode === 'text') {
            if (props?.brandBg) {
                return (
                    <div className={s['brandImageWrapper']}>
                        <picture>
                            <source
                                media="(max-width: 768px)"
                                srcSet={getFastlyUrl({
                                    src: brandBgMobile
                                        ? brandBgMobile
                                        : brandBg,
                                    quality: imageQuality,
                                    width: 800
                                })}
                            />
                            <source
                                media="(min-width: 769px) and (max-width: 1600px)"
                                srcSet={getFastlyUrl({
                                    src: brandBg,
                                    quality: imageQuality,
                                    width: 1300
                                })}
                            />
                            <source
                                media="(min-width: 1601px)"
                                srcSet={getFastlyUrl({
                                    src: brandBg,
                                    quality: imageQuality,
                                    width: 1600
                                })}
                            />
                            <img
                                src={getFastlyUrl({
                                    src: brandBg,
                                    quality: imageQuality,
                                    width: 1600
                                })}
                                loading="lazy"
                                /* eslint-disable-next-line i18next/no-literal-string */
                                alt="Shisha World - Product Slider Backround"
                                className={s['brand-img-with-carousel']}
                            />
                        </picture>
                    </div>
                )
            }
            return (
                <div
                    className={cn(
                        s['brandImageColorWrapper'],
                        s[props.upperColor]
                    )}
                />
            )
        }
    }

    const handleLoadMore = () => {
        setCurrentPage((prev) => prev + 1)
    }

    const renderCarouselOrGrid = () => {
        if (appearance === 'product_carousel') {
            const galleryItems = productsToRender.map((item, index) => (
                <ProductTile
                    product={item}
                    className="sliderItem"
                    key={index + item?.sku}
                />
            ))
            const isShowAsGrid = () => {
                if (isMobile) {
                    return props.isProductGridEnabledOnMobile
                }
                if (isTablet) {
                    return props.isProductGridEnabledOnTablet
                }
                return props.isProductGridEnabledOnDesktop
            }
            if (!isShowAsGrid()) {
                let carouselSettings = {
                    slidesToShow: 4,
                    slidesToScroll: 1,
                    infinite: false,
                    arrows: true,
                    dots: false,
                    responsive: [
                        {
                            breakpoint: 1024,
                            settings: {
                                slidesToShow: 2.4,
                                slidesToScroll: 2,
                                arrows: true
                            }
                        },
                        {
                            breakpoint: 820,
                            settings: {
                                slidesToShow: 2.4,
                                slidesToScroll: 2,
                                arrows: true
                            }
                        },
                        {
                            breakpoint: 609,
                            settings: {
                                slidesToShow: 1.5,
                                slidesToScroll: 1,
                                arrows: false
                            }
                        }
                    ]
                }
                return (
                    <SlickSlider {...carouselSettings}>
                        {galleryItems}
                    </SlickSlider>
                )
            }
        }

        return (
            <>
                <Gallery
                    AddToWishlist={AddToWishlist}
                    AddToWishlistLabel={AddToWishlistLabel}
                    items={productsToRender}
                    globalOptions={globalOptions}
                    ProductTile={ProductTile}
                />

                {props.isLoadMoreEnabled && isValidating && currentPage > 1 && (
                    <LoaderComponent />
                )}
                {props.isLoadMoreEnabled && hasMoreSkus && (
                    <div className={s['view-all-button']}>
                        <Button
                            variant="primary"
                            onClick={handleLoadMore}
                            disabled={isValidating}
                        >
                            {isValidating ? t('Loading...') : t('Show More')}
                        </Button>
                    </div>
                )}
            </>
        )
    }

    if (!enabled) {
        return <></>
    }

    return (
        <div className={cn(s.productSlider, 'productSlider')}>
            {renderLogoOrText()}
            <div className={cn(s.productSliderDefault, 'productSliderDefault')}>
                {renderContents()}
                {renderCarouselOrGrid()}
                {!props.isLoadMoreEnabled && (
                    <div className={s['view-all-button']}>
                        {props?.brandButtonText && props?.brandButtonText && (
                            <Link href={props.brandButtonLink} passHref>
                                <a
                                    className={cn(
                                        'btn-primary',
                                        s['brand-view-all-button']
                                    )}
                                >
                                    {props?.brandButtonText}
                                </a>
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

/**
 * Props for {@link Products}
 *
 * @typedef props
 * @property {Array} skus List of SKUs to load into product list
 */
Products.propTypes = {
    appearance: oneOf(['product_grid', 'product_carousel']),
    carouselMode: oneOf(['default', 'continuous']),
    centerPadding: string,
    skus: arrayOf(string),
    globalOptions: object
}

Products.defaultProps = {
    productQuery: GET_PRODUCTS_BY_SKU_QUERY
}

export default Products
